import { CONTRACTOR_BADGE_VERIFICATION_STATUS } from "@clipboard-health/contract-backend-main";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useQueryClient } from "@tanstack/react-query";
import { HttpStatusCode, isAxiosError } from "axios";
import { useCallback } from "react";

import { useCreateContractorBadgeVerification } from "../api/useCreateContractorBadgeVerification";
import { invalidateContractorBadgeVerification } from "../api/useGetContractorBadgeVerification";
import { INVALIDATE_ALL_BADGE_QUERIES_KEY } from "../api/useGetWorkerBadgeVerifications";
import { useUpdateContractorBadgeVerification } from "../api/useUpdateContractorBadgeVerification";
import { useUploadBadgeVerificationPhoto } from "../api/useUploadBadgeVerificationPhoto";
import { BADGE_VERIFICATION_FLOW } from "../constants";
import { type BadgeVerificationPhoto } from "../types";

export type BadgeVerificationSubmitResult =
  | { status: "success"; shouldPollForGrade: boolean }
  | { status: "alreadyCompleted" }
  | { status: "error" };

interface UseBadgeVerificationSubmitProps {
  shiftId: string;
  /**
   * Called after a successful photo submission. Optional because the redesign
   * dialog manages clock-in navigation separately (via the grade result screen),
   * while the legacy dialog still relies on this callback.
   */
  onSubmitPhoto?: () => void;
  onSkipSuccess: () => void;
}

export function useBadgeVerificationSubmit(props: UseBadgeVerificationSubmitProps) {
  const { shiftId, onSubmitPhoto, onSkipSuccess } = props;

  const queryClient = useQueryClient();
  const { showErrorToast, showInfoToast } = useToast();

  const { mutateAsync: createVerification, isLoading: isCreatingVerification } =
    useCreateContractorBadgeVerification();
  const { mutateAsync: updateVerification, isLoading: isUpdatingVerification } =
    useUpdateContractorBadgeVerification();
  const { mutateAsync: uploadToS3, isLoading: isUploadingToS3 } = useUploadBadgeVerificationPhoto();

  const isSubmitting = isCreatingVerification || isUploadingToS3 || isUpdatingVerification;

  // Verification already completed server-side (e.g., user navigated away mid-submission).
  const handleAlreadyCompleted = useCallback(async () => {
    logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_ALREADY_COMPLETED, { shiftId });
    await invalidateContractorBadgeVerification(queryClient, shiftId);
    showInfoToast("Badge verification was already completed.");
  }, [queryClient, shiftId, showInfoToast]);

  const handleSubmit = useCallback(
    async (photo: BadgeVerificationPhoto): Promise<BadgeVerificationSubmitResult> => {
      try {
        // Step 1: Create verification record and get presigned S3 upload URL
        const { data: verification } = await createVerification({ shiftId });
        const { uploadUrl } = verification.attributes;
        const verificationId = verification.id;

        // Step 2: Upload photo to S3 using presigned URL
        await uploadToS3({ uploadUrl, photoBlob: photo.blob });

        // Step 3: Confirm the upload by updating verification status.
        // A 202 response means AI grading is in progress and the frontend should poll.
        const { responseStatus } = await updateVerification({
          verificationId,
          status: CONTRACTOR_BADGE_VERIFICATION_STATUS.UPLOADED,
          shiftId,
        });

        logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_PHOTO_SUBMITTED, { shiftId });
        onSubmitPhoto?.();
        return {
          status: "success",
          shouldPollForGrade: responseStatus === HttpStatusCode.Accepted,
        };
      } catch (error) {
        // A 422 means the verification was already completed server-side (e.g., the user
        // navigated away mid-submission but the request succeeded). Invalidate the cache
        // so the UI reflects the completed state, then proceed with clock-in.
        if (isAxiosError(error) && error.response?.status === 422) {
          await handleAlreadyCompleted();
          logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_PHOTO_SUBMITTED, { shiftId });
          onSubmitPhoto?.();
          return { status: "alreadyCompleted" };
        }

        logError(APP_V2_APP_EVENTS.BADGE_VERIFICATION_SUBMIT_FAILURE, {
          error,
          metadata: { shiftId, flow: BADGE_VERIFICATION_FLOW.UPLOAD },
        });
        showErrorToast("Failed to submit badge photo. Please try again.");
        return { status: "error" };
      }
    },
    [
      shiftId,
      createVerification,
      uploadToS3,
      updateVerification,
      onSubmitPhoto,
      showErrorToast,
      handleAlreadyCompleted,
    ]
  );

  const handleSkipToClockIn = useCallback(async () => {
    try {
      // Step 1: Create verification record (idempotent - will return existing if exists)
      const createResponse = await createVerification({ shiftId });
      const verificationId = createResponse.data.id;

      // Step 2: Mark verification as skipped
      await updateVerification({
        verificationId,
        status: CONTRACTOR_BADGE_VERIFICATION_STATUS.SKIPPED,
        shiftId,
      });

      logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_SKIPPED, { shiftId });
      await queryClient.invalidateQueries(INVALIDATE_ALL_BADGE_QUERIES_KEY);
      onSkipSuccess();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        await handleAlreadyCompleted();
        logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_SKIPPED, { shiftId });
        await queryClient.invalidateQueries(INVALIDATE_ALL_BADGE_QUERIES_KEY);
        onSkipSuccess();
        return;
      }

      logError(APP_V2_APP_EVENTS.BADGE_VERIFICATION_SUBMIT_FAILURE, {
        error,
        metadata: { shiftId, flow: BADGE_VERIFICATION_FLOW.SKIP },
      });
      showErrorToast("Failed to skip badge verification. Please try again.");
    }
  }, [
    shiftId,
    createVerification,
    updateVerification,
    queryClient,
    showErrorToast,
    handleAlreadyCompleted,
    onSkipSuccess,
  ]);

  return { handleSubmit, handleSkipToClockIn, isSubmitting };
}
